import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ListingCategoryConst, PrefTypeConst, parseCombinedCategory, CombinedCategoryOptions } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";
import { useItemFilters } from "../../contexts/ItemFilterContext";
import { useMasters } from "../../contexts/MastersContext";
import { formatToDateInput, formatFromDateInput } from "../../utils/date";
import { useBiddingItemsGetAllRoleItemListQuery } from "../../hooks/useBiddingInfoQueryAllRole";
import { BiddingInputFilterDialog } from "../../components/buyer/BiddingInput/BiddingInputFilterDialog";
import { BiddingMinPriceSettingItemRow } from "../../components/admin/BiddingMinPriceSetting/BiddingMinPriceSettingItemRow";
import { useUpdateSubItemBidMinPriceMutation } from "../../hooks/useBiddingItemsQuery";
import { UserRole } from "../../types/UserRole";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useGetBiddingDateForApplyQuery } from "../../hooks/useCalendarQuery";
import { RefreshCw } from "lucide-react";

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");


const BiddingMinPriceSetting = () => {
  //----------------
  // Context使用定義
  //----------------
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const {
    isMastersLoading,
    sellers,
    farmers,
    teaTypes,
    teaSeasons,
    varieties,
    gaps,
  } = useMasters();
  const [] = useSearchParams();
  // 初期化 で「どの日付を入れるべきか」が判別
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  //----------------
  // State定義
  //----------------
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isSeller = currentUser?.role === UserRole.Seller;
  const isJA = currentUser?.role === UserRole.JA;
  
  //----------------
  // Memo定義
  //----------------
  const masterDataForComponents = useMemo(
    () => ({
      varieties,
      teaTypes,
      teaSeasons,
      sellers,
      farmers,
      gaps,
    }),
    [varieties, teaTypes, teaSeasons, sellers, farmers, gaps],
  );

  const biddingDateFilter =
    itemFilters.biddingDate || new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (isDateLoading || isMastersLoading || !currentUser || isInitialized) return;

    let defaultPref: number = PrefTypeConst.INTERNAL; 
    if (isSeller && currentUser.masterCode) {
      const myData = sellers.find(s => String(s.sellerCode) === currentUser.masterCode);
      if (myData) defaultPref = myData.prefectureType;
    }

    const newFilters = { ...itemFilters };
    let hasChanged = false;

    if (!newFilters.biddingDate) {
      newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
      hasChanged = true;
    }
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }
    if (!newFilters.prefectureType) {
      newFilters.prefectureType = defaultPref;
      hasChanged = true;
    }

    //売手なら自分のコードで固定。管理者なら空("")のままで全件検索。
    if (isSeller && !newFilters.sellerCode) {
      newFilters.sellerCode = currentUser?.parentCode ? Number(currentUser.parentCode) : "";
      hasChanged = true;
    }

    if (hasChanged) updateFiltersAndNavigate(newFilters);
    setIsInitialized(true); 
  }, [initialBiddingDate, isDateLoading, isMastersLoading, sellers, currentUser, itemFilters, isInitialized, updateFiltersAndNavigate, isSeller]);
  
  //----------------
  // Hooks使用定義
  //----------------
  const queryClient = useQueryClient();
  const isSearchEnabled = !!itemFilters.biddingDate && !!itemFilters.listingCategory;

  // 1. データ取得
  const { data: responseData, isLoading: isItemsLoading, isFetching, error } = useBiddingItemsGetAllRoleItemListQuery(
    itemFilters,
    currentUser?.loginId ?? "",
    { enabled: isInitialized }
  );

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['listingSchedule', 'all'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  const updateMinPriceMutation = useUpdateSubItemBidMinPriceMutation();

  // 表示用フィルタリング
  const validItems = useMemo(() => {
    if (!responseData || !responseData.items) return [];
    return responseData.items;
  }, [responseData]);

  // 合体セレクトボックス用ハンドラー
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
  if (selectedValue === "") {
    // 「すべて」が選ばれた場合、区分も地区も検索条件から外す
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: undefined, 
      prefectureType: undefined, 
    });
    return;
  }

  const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
  updateFiltersAndNavigate({
    ...itemFilters,
    listingCategory: listingCategory,
    prefectureType: prefCategory, 
  });
  }, [itemFilters, updateFiltersAndNavigate]);
  
  //----------------
  // CallBack関数定義
  //----------------

  const handleToggleSelectRow = useCallback(
    (itemId: string, branchNumber: number) => {
      const key = `${itemId}_${branchNumber}`;
      setSelectedKey((prevKey) => (prevKey === key ? null : key));
    },
    [],
  );

  const handleBiddingDateChange = useCallback(
    (dateValue: string) => {
      const newDate = formatFromDateInput(dateValue);
      updateFiltersAndNavigate({
        ...itemFilters,
        biddingDate: newDate,
      });
    },
    [itemFilters, updateFiltersAndNavigate],
  );

  //最低落札値更新ハンドラ
  const handleSubmitMinPrice = (data: {
    itemId: string;
    branchNumber: number;
    bidMinPrice: number;
  }) => {
    updateMinPriceMutation.mutate(
      {
        itemId: data.itemId,
        branchNumber: data.branchNumber,
        bidMinPrice: data.bidMinPrice,
      },
      {
        onSuccess: () => {
          //再読み込みさせて最新の最低落札値を表示させる
          queryClient.invalidateQueries({ queryKey: ["listingSchedule"] });
          setSelectedKey(null);
        },
        onError: (err) => {
          console.error(err);
        },
      },
    );
  };

  //----------------
  // その他定数定義
  //----------------
  const isPageLoading = (isItemsLoading && !responseData) || isMastersLoading || isDateLoading || !isInitialized; 

  //----------------
  // レンダリング処理
  //----------------
  if (isPageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>読み込み中...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-red-100 p-4 rounded text-red-700">
          エラー: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* --- ヘッダーエリア --- */}
        <header className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-extrabold text-slate-800 lg:mb-1.5">
              最低落札値の決定
            </h1>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition border border-indigo-200"
            >
              詳細検索
            </button>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-wrap gap-6 items-start">
            {/* 1. 入札日 */}
            <div className="w-full sm:w-auto">
              <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">
                入札日
              </label>
              <input
                type="date"
                value={formatToDateInput(biddingDateFilter)}
                onChange={(e) => handleBiddingDateChange(e.target.value)}
                className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none min-w-0 safari-date-fix"
              />
            </div>
            {/* 2. 出品区分 */}
            <div className="w-full sm:w-auto">
              <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
              <select
                disabled={isSeller || isJA} // 売手・JAなら固定
                // URLにある「区分」と「地区」をくっつけて現在の選択状態を表示
                value={
                  itemFilters.listingCategory && itemFilters.prefectureType
                    ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                    : ""
                }
                onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                className={`w-full sm:w-56 p-2 h-[42px] border border-slate-300 rounded-md ${isSeller || isJA ? "bg-slate-100 text-slate-700" : "bg-white"}`}
              >
                {/* <option value="">すべて</option> */}
                {CombinedCategoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </header>

        <main>
          {!isSearchEnabled ? (
            <div className="text-center py-10 text-gray-500">
              入札日と出品区分を選択してください。
            </div>
          ) : (isItemsLoading && !responseData) ? (
            <div className="text-center py-10 text-gray-500">
              読み込み中...
            </div>
          ) : (responseData && !responseData.isSuccess) ? (
            <div className="text-center py-10 text-gray-500">
              {responseData.message}
            </div>
          ) : (validItems.length === 0) ? (
            <div className="text-center py-10 text-gray-500">
              表示する出品情報がありません。
            </div>
          ) : (
            <div className="space-y-4">
              {validItems.map((item) => {
                  const rowKey = `${item.itemId}_${item.branchNumber}`;
                  return (
                    <BiddingMinPriceSettingItemRow
                      key={rowKey}
                      item={item}
                      isSelected={selectedKey === rowKey}
                      onToggleSelect={() =>
                        handleToggleSelectRow(item.itemId, item.branchNumber)
                      }
                      onSubmit={handleSubmitMinPrice}
                      isSaving={updateMinPriceMutation.isPending}
                    />
                  );
                })
              }
            </div>
          )}
        </main>

        <BiddingInputFilterDialog
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          masterData={masterDataForComponents}
          isSeller = {isSeller}
          isFarmer = {false}
        />
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-50 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default BiddingMinPriceSetting;
